import { React, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';
import { socialLinks } from './data/social';
import { site } from './data/site';

export const Contacts = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const [disabled, setDisabled] = useState(false);

  // Time the form has been on screen, sent as elapsedMs. The service treats anything
  // under 3 seconds as a bot, so a fast autofill-and-submit waits out the remainder.
  const shownAt = useRef(performance.now());
  const MIN_FILL_MS = 3000;
  const contactApi = process.env.REACT_APP_CONTACT_API;
  const msgs = site.contactMessages;

  const [alertInfo, setAlertInfo] = useState({
    display: false,
    message: '',
    type: '',
    mailto: '',
  });

  // Shows alert message for form submission feedback
  const toggleAlert = (message, type, mailto = '') => {
    setAlertInfo({ display: true, message, type, mailto });

    // Success fades after 5 seconds. Errors stay until closed so the email fallback can be used.
    if (type === 'success') {
      setTimeout(() => {
        setAlertInfo({ display: false, message: '', type: '', mailto: '' });
      }, 5000);
    }
  };

  // POSTs to the contact service. Returns true when the message was accepted.
  const sendViaApi = async (data) => {
    const wait = MIN_FILL_MS + 100 - (performance.now() - shownAt.current);
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
    const fallback = socialLinks.find((l) => l.id === 'email').href;
    let res;
    try {
      res = await fetch(`${contactApi}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          subject: data.subject,
          message: data.message,
          website: data.contactHp || '',
          elapsedMs: performance.now() - shownAt.current,
        }),
      });
    } catch (e) {
      toggleAlert(msgs.unavailable, 'danger', fallback);
      return false;
    }
    if (res.ok) {
      toggleAlert(msgs.success, 'success');
      return true;
    }
    let body = {};
    try {
      body = await res.json();
    } catch (e) {
      // non-JSON error body: fall through to the generic messages
    }
    if (res.status === 429) toggleAlert(msgs.rateLimited, 'danger', body.fallback || fallback);
    else if (res.status === 400) {
      toggleAlert(Object.values(body.fields || {})[0] || msgs.failed, 'danger');
    } else toggleAlert(msgs.unavailable, 'danger', body.fallback || fallback);
    return false;
  };

  // Function called on submit that uses emailjs to send email of valid contact form
  const onSubmit = async (data) => {
    // Destrcture data object
    const { name, email, subject, message } = data;
    let sent = false;
    try {
      // Disable form while processing submission
      setDisabled(true);

      if (contactApi) {
        sent = await sendViaApi(data);
        return;
      }

      // Rollback path: no REACT_APP_CONTACT_API at build time, so use EmailJS as before.
      // Define template params
      const templateParams = {
        name,
        email,
        subject,
        message,
      };

      // Use emailjs to email contact form data
      await emailjs.send(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_PUBLIC_KEY
      );

      // Display success alert
      toggleAlert(msgs.sent, 'success');
      sent = true;
    } catch (e) {
      console.error(e);
      // Display error alert
      toggleAlert(msgs.failed, 'danger');
    } finally {
      // Re-enable form submission
      setDisabled(false);
      // Clear the fields only once the message went through, so a failure never loses it
      if (sent) {
        reset();
        shownAt.current = performance.now();
      }
    }
  };

  return (
    <section id="contact" aria-labelledby="contact-title" className="section contactForm">
      <div className="container">
        <h2 id="contact-title" className="section-title">
          Get in touch
        </h2>
        <div className="row g-4">
          <div className="col-12 col-lg-7">
            <form id="contact-form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="field-label" htmlFor="contact-name">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    {...register('name', {
                      required: {
                        value: true,
                        message: 'Please enter your name',
                      },
                      maxLength: {
                        value: 30,
                        message: 'Please use 30 characters or less',
                      },
                    })}
                    className="form-control formInput"
                    id="contact-name"
                    autoComplete="name"
                    aria-invalid={errors.name ? 'true' : 'false'}
                    aria-describedby={errors.name ? 'contact-name-error' : undefined}
                  />
                  {errors.name && (
                    <span className="errorMessage" id="contact-name-error" role="alert">
                      {errors.name.message}
                    </span>
                  )}
                </div>
                <div className="col-12 col-md-6">
                  <label className="field-label" htmlFor="contact-email">
                    Email address
                  </label>
                  <input
                    type="email"
                    name="email"
                    {...register('email', {
                      required: true,
                      pattern:
                        /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                    })}
                    className="form-control formInput"
                    id="contact-email"
                    autoComplete="email"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'contact-email-error' : undefined}
                  />
                  {errors.email && (
                    <span className="errorMessage" id="contact-email-error" role="alert">
                      Please enter a valid email address
                    </span>
                  )}
                </div>
                <div className="col-12">
                  <label className="field-label" htmlFor="contact-subject">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    {...register('subject', {
                      required: {
                        value: true,
                        message: 'Please enter a subject',
                      },
                      maxLength: {
                        value: 75,
                        message: 'Subject cannot exceed 75 characters',
                      },
                    })}
                    className="form-control formInput"
                    id="contact-subject"
                    aria-invalid={errors.subject ? 'true' : 'false'}
                    aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
                  />
                  {errors.subject && (
                    <span className="errorMessage" id="contact-subject-error" role="alert">
                      {errors.subject.message}
                    </span>
                  )}
                </div>
                <div className="col-12">
                  <label className="field-label" htmlFor="contact-message">
                    Message
                  </label>
                  <textarea
                    rows={5}
                    name="message"
                    {...register('message', {
                      required: true,
                      minLength: { value: 10, message: msgs.messageTooShort },
                      maxLength: { value: 5000, message: msgs.messageTooLong },
                    })}
                    className="form-control formInput"
                    id="contact-message"
                    aria-invalid={errors.message ? 'true' : 'false'}
                    aria-describedby={errors.message ? 'contact-message-error' : undefined}
                  ></textarea>
                  {errors.message && (
                    <span className="errorMessage" id="contact-message-error" role="alert">
                      {errors.message.message || 'Please enter a message'}
                    </span>
                  )}
                </div>
                {/* Honeypot: hidden from people and screen readers, filled by bots. The field
                    is deliberately not named "website" so browser autofill leaves it alone. */}
                <div className="hp-field" aria-hidden="true">
                  <input
                    type="text"
                    id="contact-hp"
                    autoComplete="off"
                    tabIndex={-1}
                    {...register('contactHp')}
                  />
                </div>
                <div className="col-12">
                  <button className="submit-btn btn btn-primary" disabled={disabled} type="submit">
                    Submit
                  </button>
                </div>
              </div>
            </form>
            {alertInfo.display && (
              <div className={`form-status form-status-${alertInfo.type}`} role="alert">
                <span>
                  {alertInfo.message}
                  {alertInfo.mailto && (
                    <>
                      {' '}
                      <a href={alertInfo.mailto}>{alertInfo.mailto.replace(/^mailto:/, '')}</a>
                    </>
                  )}
                </span>
                <button
                  type="button"
                  className="form-status-close"
                  aria-label="Close"
                  onClick={() =>
                    setAlertInfo({ display: false, message: '', type: '', mailto: '' })
                  } // Clear the alert when close button is clicked
                >
                  <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
          <div className="col-12 col-lg-5">
            <div className="pane contact-pane">
              <div className="pane-strip">
                <span>channels</span>
              </div>
              <p className="contact-intro">{site.contactIntro}</p>
              <dl className="kv">
                {socialLinks.map((link) => (
                  <div className="kv-row" key={link.id}>
                    <dt>{link.label.toLowerCase()}</dt>
                    <dd>
                      <a
                        href={link.href}
                        {...(link.href.startsWith('mailto:')
                          ? {}
                          : { target: '_blank', rel: 'noopener noreferrer' })}
                      >
                        {link.href.replace(/^mailto:|^https:\/\/(www\.)?/, '').replace(/\/$/, '')}
                      </a>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
